import { Big } from '@blockworks/platform/libs/big';

import type { SankeyLinkDatum, SankeyNodeDatum, SankeyProps } from '../sankey.model';

import { getSumFromRange } from './get-sum-from-range';

type UseSankeyOptions = Pick<SankeyProps, 'options' | 'activeControls' | 'params' | 'data'>;

const makeSankeyRootData = ({ data, activeControls, options, params }: UseSankeyOptions) => {
    /** array to track index of each node */
    const nodeNames: string[] = [];
    /** final array of nodes */
    const nodes: SankeyNodeDatum[] = [];
    /** final array of links */
    const links: SankeyLinkDatum[] = [];

    const activeGroupParam = params?.group ?? activeControls?.groups?.initialValue;
    const activeGroup = options?.groups?.find?.(group => group.name === activeGroupParam) ?? options?.groups?.[0];

    if (!Array.isArray(data) || !activeGroup) {
        console.error('Sankey data error. Data or active group is invalid.', { data, activeGroup });
        return { nodes, links };
    }

    const groupLinks = activeGroup?.links;
    groupLinks?.forEach(link => {
        const rawValue = data?.[link.rowIndex]?.[link.columnKey];

        if (typeof rawValue !== 'number' && typeof rawValue !== 'string') {
            console.error(`Data error: ${link.columnKey} is invalid at index ${link.rowIndex}`);
            return;
        }

        if (!link.target || !link.source) {
            return;
        }

        let value: number = Big(rawValue).toNumber();

        /**
         * precalculation for sum-range links so a runtime sum value can be determined,
         * and the value negation condition (line 49) doesn't exit early.
         **/
        if (link.sumRange?.enabled) {
            const sum = getSumFromRange(link, data);
            if (typeof sum === 'number') {
                value = sum;
            }
        }

        if (!value || value <= 0) {
            console.log(`Skipping link due to a 0 value for "${link.source} -> ${link.target}"`);
            return;
        }

        let sourceIndex = nodeNames.indexOf(link.source);
        if (sourceIndex === -1) {
            nodeNames.push(link.source);
            nodes.push({ name: link.source, color: link.color });
            sourceIndex = nodeNames.length - 1;
        }

        let targetIndex = nodeNames.indexOf(link.target);
        if (targetIndex === -1) {
            nodeNames.push(link.target);
            nodes.push({ name: link.target, color: link.color });
            targetIndex = nodeNames.length - 1;
        }

        // given ALL the links, see if this link is circular
        const foundCircularLink = groupLinks.find(
            groupLink => groupLink.target === link.source && groupLink.source === link.target,
        );

        // Skip links that refer to themselves (results in circular reference error in visx)
        if (sourceIndex !== targetIndex && !foundCircularLink) {
            links.push({
                source: sourceIndex,
                target: targetIndex,
                value,
                valueFormat: link.valueFormat,
            });
        }
    });

    return { nodes, links };
};

export { makeSankeyRootData };
